import React, { useState, useEffect, useMemo } from "react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { GatsbyImage } from "gatsby-plugin-image";

import { ReferenciaCabecera } from "../../../interfaces/ReferenciaCabecera";
import { getFormatDate } from "../../../shared/utils/dateUtilities";
import { normalizeTwoWords, formatImageAlt } from "../../../shared/utils/stringUtilities";
import ShareModal from "../../molecules/share-modal/shareModal";
import Breadcrumb from "../../atoms/breadcrumb/breadcrumb";
import Button from "../../atoms/button/button";
import useBrowserMode from "../../../shared/hooks/useBrowserMode";
import useScrollDetect from "../../../shared/hooks/useScrollDetect";
import useDirectionScroll from "../../../shared/hooks/useDirectionScroll";

import "./headboardTabs.scss";

const HeadboardTabs = (props: ReferenciaCabecera) => {
  const { isBrowser, window, document } = useBrowserMode();
  const [tabSelect, setTabSelect] = useState(-1);
  const [upScroll, setUpScroll] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { isScrollActive, refElement } = useScrollDetect();
  const { lastDirection } = useDirectionScroll();

  const doCssChatBot = () => {
    const chatBot = document.body.querySelector(
      ".RecastAppExpander.CaiAppExpander.open"
    );
    chatBot?.classList.add("open-service");
  };

  useEffect(doCssChatBot, []);

  useEffect(() => {
    isBrowser && document.addEventListener("scroll", onScrollAction);
    return () => {
      isBrowser && document.removeEventListener("scroll", onScrollAction);
    };
  }, [upScroll]);

  useMemo(() => {
    props[0].superFooter?.set(isScrollActive);
  }, [isScrollActive]);

  const refTags = (props[0].referenciaTags || []).map((rt) => rt.localizador);

  const goToContent = (index: number) => {
    if (isBrowser && refTags.length) {
      const element = document.getElementById(
        props[0].tipoCabecera + refTags[index][0]
      );
      setUpScroll(true);
      element?.scrollIntoView(true);
    }
  };

  const getCurrentPage = () => {
    const arr = props[0].migaDePan.split("/");
    return arr[arr.length - 1].trim();
  };

  let timerScroll: any;

  const verifyTabs = (index: number) => {
    const indexString = `${index}`;
    for (let i = 0; i < refTags.length; i++) {
      const indexSearch = refTags[i]?.find((rt) => rt === indexString);
      if (indexSearch !== undefined) {
        setTabSelect(i);
        return;
      }
    }
    setTabSelect(-1);
  };

  const onScrollAction = () => {
    window.clearTimeout(timerScroll);
    timerScroll = setTimeout(() => {
      if (upScroll) {
        window.scroll(0, window.scrollY - (isScrollActive ? 138 : 220));
        setUpScroll(false);
      }
      const tabs = document.querySelectorAll(".o-headboard-tabs__tabs__tab");
      const items = document.querySelectorAll(`.${props[0].tipoCabecera}`);
      for (let i = 0; i < items.length; i++) {
        const bottom = items[i].getBoundingClientRect().bottom;
        const top = items[i].getBoundingClientRect().top;
        if (bottom > 139 && top <= 139) {
          verifyTabs(i);
          tabs.item(i)?.scrollIntoView(true);
          return;
        }
      }
      setTabSelect(-1);
    }, 80);
  };

  const toggleModal = () => {
    setIsModalVisible(!isModalVisible);
  };

  const isTabsNoShare = !props[0].tituloBoton && !props[0].botonCompartir;

  return (
    <div className="o-headboard-tabs" ref={refElement}>
      <Breadcrumb content={props[0].migaDePan} />
      <div
        className={`o-headboard-tabs__container${
          isTabsNoShare ? " o-headboard-tabs__container--no-share" : ""
        }`}
      >
        <section className="o-headboard-tabs__container__content">
          <span className="o-headboard-tabs__container__content__title-span">
            {props[0].tituloSpan}
          </span>
          <h1
            className={`o-headboard-tabs__container__content__title${
              isTabsNoShare
                ? " o-headboard-tabs__container__content__title--no-share"
                : ""
            }`}
          >
            {props[0].tituloPrincipal}
          </h1>
          {props[0].verFechaPublicacion && (
            <span className="o-headboard-tabs__container__content__date">
              {getFormatDate(props[0].updatedAt, 0)}
            </span>
          )}
          {props[0].descripcion && (
            <article className="o-headboard-tabs__container__content__text">
              {documentToReactComponents(props[0].descripcion.json)}
            </article>
          )}
          {props[0].botonCompartir && (
            <button
              className={`o-headboard-tabs__container__content__share gtm${normalizeTwoWords(
                getCurrentPage()
              )}Compartir`}
              onClick={toggleModal}
            >
              <i className="icon-share"></i>
              {props[0].botonCompartir}
            </button>
          )}
        </section>
        <div
          className={`o-headboard-tabs__container__cover${
            isTabsNoShare ? " o-headboard-tabs__container__cover--hide-m" : ""
          }`}
        >
          {props[0].imagenHeader.gatsbyImageData ?
            <GatsbyImage
              className="o-headboard-tabs__container__cover__img"
              image={props[0].imagenHeader.gatsbyImageData}
              alt={formatImageAlt(props[0].imagenHeader.title) || "Imagen cabecera tabs"}
            />
            :
            <img
              className="o-headboard-tabs__container__cover__img"
              src={props[0].imagenHeader.file.url}
              alt={formatImageAlt(props[0].imagenHeader.title) || "Imagen cabecera tabs"}
            />
          }
        </div>
        {props[0].botonCompartir && (
          <button
            className={`o-headboard-tabs__container__share gtm${normalizeTwoWords(
              getCurrentPage()
            )}Compartir`}
            onClick={toggleModal}
          >
            <i className="icon-share"></i>
            {props[0].botonCompartir}
          </button>
        )}
      </div>
      {isScrollActive && (
        <div
          className={`o-headboard-tabs__top${
            isTabsNoShare ? " o-headboard-tabs__top--hide-m" : ""
          }`}
        >
          <span className="o-headboard-tabs__top__title">
            {props[0].tituloPrincipal}
          </span>
          <div className="o-headboard-tabs__top__buttons">
            {props[0].tituloBoton && (
              <Button
                classname="o-headboard-tabs__top__buttons__button-one"
                type={"link"}
                href={props[0].redireccionBoton}
                text={props[0].tituloBoton}
                isDisabled={false}
                gtmMark={`gtm${normalizeTwoWords(
                  getCurrentPage()
                )}${normalizeTwoWords(props[0].tituloBoton)}`}
                strictCss={true}
              />
            )}
            {props[0].botonCompartir && (
              <button
                className={`o-headboard-tabs__top__buttons__button-two icon-share gtm${normalizeTwoWords(
                  getCurrentPage()
                )}Compartir`}
                onClick={toggleModal}
              ></button>
            )}
          </div>
        </div>
      )}
      {props[0].referenciaTags && (
        <ul
          className={`o-headboard-tabs__tabs${
            isScrollActive ? " tabs-fixed" : ""
          }${lastDirection == -1 ? " tabs-fixed-move" : ""}`}
        >
          {props[0].referenciaTags.map((value, index) => (
            <li
              key={`headerTab-${index}`}
              className={`gtm${normalizeTwoWords(
                getCurrentPage()
              )}${normalizeTwoWords(
                value.nombreTag
              )} o-headboard-tabs__tabs__tab${
                index === tabSelect ? " tab-active" : ""
              }`}
              onClick={() => goToContent(index)}
            >
              {value.nombreTag}
            </li>
          ))}
        </ul>
      )}
      <ShareModal open={isModalVisible} onClose={toggleModal} />
    </div>
  );
};

export default HeadboardTabs;
